import { useState, useEffect, useCallback } from "react";
import { workflowApi, type ContractWorkflow, type WorkflowStep, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function initials(name: string): string {
  return name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function isOverdue(step: WorkflowStep): boolean {
  if (step.status !== "in_progress" || !step.started_at) return false;
  const days = (Date.now() - new Date(step.started_at).getTime()) / (1000 * 60 * 60 * 24);
  return days >= 14;
}

const STEP_ICON: Record<WorkflowStep["status"], string> = {
  completed: "ri-checkbox-circle-fill text-secondary-500",
  skipped: "ri-forbid-2-line text-foreground-400",
  in_progress: "ri-record-circle-fill text-primary-500",
  pending: "ri-checkbox-blank-circle-line text-foreground-300",
};

const FINAL_DECISION_STAGE = "Renewal Approved OR New Procurement Required";

export default function RenewalWorkflowTab({ contractId }: { contractId: number }) {
  const { user } = useAuth();
  const [workflow, setWorkflow] = useState<ContractWorkflow | null>(null);
  const [completionPct, setCompletionPct] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(false);
  const [busyStepId, setBusyStepId] = useState<number | null>(null);
  const [decisionChoice, setDecisionChoice] = useState<"approved" | "new_procurement">("approved");
  const [stepComment, setStepComment] = useState("");
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  const canManage = user?.role === "scm_manager" || user?.role === "admin";
  const canDecide = user?.role === "cfo_hod" || user?.role === "admin";

  const load = useCallback(() => {
    setLoading(true);
    workflowApi
      .get(contractId)
      .then((res) => {
        setWorkflow(res.data);
        setCompletionPct(res.completion_percentage ?? 0);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load workflow"))
      .finally(() => setLoading(false));
  }, [contractId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleStart = async () => {
    setStarting(true);
    setError("");
    try {
      await workflowApi.start(contractId);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to start workflow");
    } finally {
      setStarting(false);
    }
  };

  const handleStepAction = async (step: WorkflowStep, action: "complete" | "skip" | "reopen") => {
    if (!workflow) return;
    setBusyStepId(step.id);
    setError("");
    try {
      await workflowApi.updateStep(workflow.id, step.id, {
        action,
        comments: stepComment.trim() || undefined,
        decision: action === "complete" && step.stage_name === FINAL_DECISION_STAGE ? decisionChoice : undefined,
      });
      setStepComment("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed");
    } finally {
      setBusyStepId(null);
    }
  };

  const handleAddComment = async () => {
    if (!workflow || !newComment.trim()) return;
    setPostingComment(true);
    try {
      await workflowApi.addComment(workflow.id, newComment.trim());
      setNewComment("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add comment");
    } finally {
      setPostingComment(false);
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !workflow) return;
    setUploadingFile(true);
    try {
      await workflowApi.uploadDocument(workflow.id, file);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploadingFile(false);
      e.target.value = "";
    }
  };

  if (loading) {
    return <div className="py-16 text-center text-foreground-400 text-sm"><i className="ri-loader-4-line animate-spin mr-2"></i>Loading workflow…</div>;
  }

  if (!workflow) {
    return (
      <div className="bg-white rounded-lg border border-background-200 p-8 text-center">
        <i className="ri-refresh-line text-foreground-300 text-4xl mb-3 block"></i>
        <p className="text-foreground-600 text-sm mb-4">No renewal workflow has been started for this contract yet.</p>
        {error && <p className="text-accent-600 text-sm mb-3">{error}</p>}
        <button onClick={handleStart} disabled={starting}
          className="h-10 px-5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 cursor-pointer">
          {starting ? "Starting…" : "Start Renewal Workflow"}
        </button>
      </div>
    );
  }

  const currentStep = workflow.steps.find((s) => s.status === "in_progress");

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-2.5 bg-accent-50 border border-accent-200 rounded-lg px-3.5 py-3">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5 shrink-0"></i>
          <p className="text-accent-700 text-sm">{error}</p>
        </div>
      )}

      {/* Progress bar */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-primary-600 text-base font-semibold">Renewal Progress</p>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
            workflow.status === "completed" ? "bg-secondary-50 text-secondary-700" : "bg-primary-50 text-primary-700"
          }`}>
            {workflow.status === "completed" ? "Completed" : "In Progress"}
          </span>
        </div>
        <div className="w-full h-2.5 bg-background-100 rounded-full overflow-hidden mb-2">
          <div className="h-full bg-primary-500 transition-all duration-500 rounded-full" style={{ width: `${completionPct}%` }}></div>
        </div>
        <p className="text-foreground-500 text-xs">
          {completionPct}% complete · Started {formatDateTime(workflow.started_at)} by {workflow.starter?.name ?? "Unknown"}
          {workflow.status === "completed" && ` · Completed ${formatDateTime(workflow.completed_at)}`}
        </p>
        {workflow.status === "completed" && (
          <button onClick={handleStart} disabled={starting}
            className="mt-3 h-9 px-4 border border-background-300 text-foreground-600 text-sm font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-60">
            {starting ? "Starting…" : "Start New Renewal Workflow"}
          </button>
        )}
      </div>

      {/* Vertical timeline */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <p className="text-primary-600 text-base font-semibold mb-5">Stages</p>
        <div className="space-y-0">
          {workflow.steps.map((step, i) => {
            const overdue = isOverdue(step);
            const isLast = i === workflow.steps.length - 1;
            const isCurrent = step.status === "in_progress";
            const isFinalDecision = step.stage_name === FINAL_DECISION_STAGE;

            return (
              <div key={step.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <i className={`${STEP_ICON[step.status]} text-xl`}></i>
                  {!isLast && <div className={`w-0.5 flex-1 min-h-[24px] ${step.status === "completed" || step.status === "skipped" ? "bg-secondary-300" : "bg-background-200"}`}></div>}
                </div>
                <div className={`flex-1 min-w-0 ${isLast ? "" : "pb-5"}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-semibold ${isCurrent ? "text-primary-700" : "text-foreground-800"}`}>{step.stage_name}</p>
                    {step.is_optional && <span className="text-[10px] font-medium text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">Optional</span>}
                    {overdue && <span className="text-[10px] font-semibold text-accent-700 bg-accent-50 px-1.5 py-0.5 rounded-full">Overdue</span>}
                  </div>

                  {step.completed_at && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[9px] font-semibold shrink-0">
                        {step.completer ? initials(step.completer.name) : "?"}
                      </div>
                      <p className="text-foreground-400 text-xs">
                        {step.status === "skipped" ? "Skipped" : "Completed"} by {step.completer?.name ?? "—"} · {formatDateTime(step.completed_at)}
                      </p>
                    </div>
                  )}
                  {step.comments && <p className="text-foreground-500 text-xs mt-1 italic">"{step.comments}"</p>}

                  {isCurrent && workflow.status === "in_progress" && (
                    <div className="mt-3 bg-background-50 rounded-lg p-3 space-y-2">
                      {isFinalDecision && (
                        <div className="flex items-center gap-3 mb-1">
                          <label className="flex items-center gap-1.5 text-xs text-foreground-700 cursor-pointer">
                            <input type="radio" checked={decisionChoice === "approved"} onChange={() => setDecisionChoice("approved")} />
                            Approve renewal
                          </label>
                          <label className="flex items-center gap-1.5 text-xs text-foreground-700 cursor-pointer">
                            <input type="radio" checked={decisionChoice === "new_procurement"} onChange={() => setDecisionChoice("new_procurement")} />
                            New procurement required
                          </label>
                        </div>
                      )}
                      <textarea
                        value={stepComment}
                        onChange={(e) => setStepComment(e.target.value)}
                        placeholder="Add a comment for this stage (optional)…"
                        rows={2}
                        className="w-full px-3 py-2 border border-background-300 rounded-md text-xs bg-white resize-none focus:outline-none focus:border-primary-400"
                      />
                      <div className="flex items-center gap-2">
                        {(!isFinalDecision || canDecide) && (
                          <button
                            onClick={() => handleStepAction(step, "complete")}
                            disabled={busyStepId === step.id || (isFinalDecision && !canDecide)}
                            className="h-8 px-3 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-md disabled:opacity-60 cursor-pointer">
                            {busyStepId === step.id ? "Saving…" : "Complete"}
                          </button>
                        )}
                        {step.is_optional && canManage && (
                          <button
                            onClick={() => handleStepAction(step, "skip")}
                            disabled={busyStepId === step.id}
                            className="h-8 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 cursor-pointer disabled:opacity-60">
                            Skip
                          </button>
                        )}
                      </div>
                      {isFinalDecision && !canDecide && (
                        <p className="text-foreground-400 text-[11px]">Only CFO/HOD can approve or reject the final renewal decision.</p>
                      )}
                    </div>
                  )}

                  {(step.status === "completed" || step.status === "skipped") && canManage && (
                    <button
                      onClick={() => handleStepAction(step, "reopen")}
                      disabled={busyStepId === step.id}
                      className="mt-1.5 text-foreground-400 hover:text-foreground-600 text-[11px] font-medium cursor-pointer disabled:opacity-60">
                      Reopen stage
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Comments panel */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <p className="text-primary-600 text-base font-semibold mb-4">Comments</p>
        <div className="flex items-start gap-2 mb-4">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment to this workflow…"
            rows={2}
            className="flex-1 px-3 py-2 border border-background-300 rounded-md text-sm bg-white resize-none focus:outline-none focus:border-primary-400"
          />
          <button onClick={handleAddComment} disabled={postingComment || !newComment.trim()}
            className="h-9 px-4 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-md disabled:opacity-60 cursor-pointer shrink-0">
            Post
          </button>
        </div>
        {workflow.comments.length === 0 ? (
          <p className="text-foreground-400 text-sm text-center py-4">No comments yet.</p>
        ) : (
          <div className="space-y-3">
            {workflow.comments.map((c) => (
              <div key={c.id} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-semibold shrink-0">
                  {c.user ? initials(c.user.name) : "?"}
                </div>
                <div>
                  <p className="text-foreground-800 text-sm">{c.comment}</p>
                  <p className="text-foreground-400 text-[11px] mt-0.5">{c.user?.name ?? "Unknown"} · {formatDateTime(c.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document list */}
      <div className="bg-white rounded-lg border border-background-200 p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-primary-600 text-base font-semibold">Documents</p>
          <label className="h-8 px-3 border border-background-300 text-foreground-600 text-xs font-medium rounded-md hover:bg-background-100 transition-colors cursor-pointer flex items-center gap-1.5">
            <i className="ri-upload-line text-sm"></i>
            <span>{uploadingFile ? "Uploading…" : "Upload"}</span>
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploadingFile} />
          </label>
        </div>
        {workflow.documents.length === 0 ? (
          <p className="text-foreground-400 text-sm text-center py-4">No documents attached yet.</p>
        ) : (
          <div className="space-y-2">
            {workflow.documents.map((d) => (
              <div key={d.id} className="flex items-center gap-2.5 p-2.5 bg-background-50 rounded-md">
                <i className="ri-file-line text-foreground-400"></i>
                <div className="flex-1 min-w-0">
                  <p className="text-foreground-800 text-sm truncate">{d.original_filename}</p>
                  <p className="text-foreground-400 text-[11px]">{d.uploader?.name ?? "Unknown"} · {formatDateTime(d.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
